import { Suspense } from 'react';

import { Tab } from '@headlessui/react';
import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { InterestOnlyExtend } from './InterestOnlyExtend';
import styles from './TopUpPanel.module.css';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { TopUpDrawDown } from '../../../dashboardWidgets/drawdownActionPanel/TopupDrawdown';
import { useGetDrawdownActionPanelData } from '../../../dashboardWidgets/drawdownActionPanel/useGetDrawdownActionPanelData';
import messages from '../DashboardActionPanel.messages';

export const DrawdownChoice = ({
    accountId,
    hideHeader
}: {
    accountId: string;
    hideHeader?: boolean;
}) => {
    const { topUpStatus } = useGetDrawdownActionPanelData(accountId);
    switch (topUpStatus) {
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            return (
                <>
                    <DEOnly>
                        {!hideHeader && (
                            <Text>
                                <FormattedMessage
                                    {...messages.extendInterestOnly}
                                />
                            </Text>
                        )}{' '}
                        <Text>
                            <FormattedMessage
                                {...messages.extendInterestOnlyWithDrawdown}
                            />
                        </Text>
                    </DEOnly>
                    <UKOnly>
                        <Text>
                            <FormattedMessage
                                {...messages.chooseInterestOnlyDrawdownType}
                            />
                        </Text>
                    </UKOnly>
                    <Tab.Group as="div" className={styles.tabGroup}>
                        <Tab.List className={styles.tabList}>
                            <Tab className={styles.tab}>
                                <FormattedMessage
                                    {...messages.tabInterestOnlyWithDrawdown}
                                />
                            </Tab>
                            <Tab className={styles.tab}>
                                <FormattedMessage
                                    {...messages.tabInterestOnly}
                                />
                            </Tab>
                        </Tab.List>
                        <Tab.Panels className={styles.panels}>
                            <Tab.Panel>
                                <ErrorBoundary
                                    fallback={() => <FallbackErrorModal />}
                                >
                                    <Suspense
                                        fallback={
                                            <div
                                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                                data-testId="loan-overview-loading"
                                            >
                                                <MainContentPending />
                                            </div>
                                        }
                                    >
                                        <TopUpDrawDown accountId={accountId} />
                                    </Suspense>
                                </ErrorBoundary>
                            </Tab.Panel>
                            <Tab.Panel>
                                <ErrorBoundary
                                    fallback={() => <FallbackErrorModal />}
                                >
                                    <Suspense
                                        fallback={
                                            <div
                                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                                data-testId="loan-overview-loading"
                                            >
                                                <MainContentPending />
                                            </div>
                                        }
                                    >
                                        <InterestOnlyExtend
                                            accountId={accountId}
                                        />
                                    </Suspense>
                                </ErrorBoundary>
                            </Tab.Panel>
                        </Tab.Panels>
                    </Tab.Group>
                </>
            );
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
            return (
                <div className={styles.interestOnlyContainer}>
                    {!hideHeader && (
                        <DEOnly>
                            <FormattedMessage
                                {...messages.extendInterestOnly}
                            />
                        </DEOnly>
                    )}
                    <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                        <Suspense
                            fallback={
                                <div
                                    className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                    data-testid="loan-overview-loading"
                                >
                                    <MainContentPending />
                                </div>
                            }
                        >
                            <InterestOnlyExtend accountId={accountId} />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            );
        default:
            return (
                <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                    <Suspense
                        fallback={
                            <div
                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                data-testid="loan-overview-loading"
                            >
                                <MainContentPending />
                            </div>
                        }
                    >
                        <TopUpDrawDown accountId={accountId} />
                    </Suspense>
                </ErrorBoundary>
            );
    }
};
